import { useMutation, useQuery } from "@apollo/client";
import CheckCircle from "@mui/icons-material/CheckCircle";
import {
  Alert,
  Box,
  Chip,
  Grid,
  Paper,
  Skeleton,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import { useAlerts } from "components/Alerts/AlertProvider";
import { mdHeight, xsHeight } from "components/AppBar";
import { GetContainerLabelQuery, LabelExportFormat, LabelFormat } from "generated-graphql/graphql";
import { useBreadcrumb } from "hooks/useBreadcrumbs";
import { useTenant } from "hooks/useTenant";
import { ReactElement, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { downloadFromUrl } from "util/download";

import {
  EXPORT_CONTAINER_LABELS_MUTATION,
  GET_CONTAINER_LABEL_QUERY,
  SET_CONTAINER_LABEL_NOTE_MUTATION,
} from "../api";
import { formatContainerDate } from "../containerDisplay";
import "./containerLabelPrint.css";
import { LabelSizeOption, PREVIEW_SIZE_BY_LABEL_SIZE } from "./labelFormState";
import { isDotProfileIncomplete } from "./labelPreviewContent";
import { LabelPreviewStage } from "./LabelPreviewStage";
import { CardTitle } from "./LabelScreenTypography";
import { PrintFormatCard } from "./PrintFormatCard";

type ContainerLabelData = NonNullable<GetContainerLabelQuery["wasteContainer"]>;

/** Toggled on `document.body` only while this screen's content is mounted.
 * `containerLabelPrint.css`'s app-wide selectors (the "hide everything but
 * the print root" rule, the label's `@page` size) are gated behind this
 * class so they only ever apply to a print started from this screen —
 * without it, they'd apply to every print in the app, since this CSS module
 * is bundled globally (this app never lazy-loads route components, so every
 * route's side-effect CSS imports end up in the same global stylesheet). */
const PRINT_ACTIVE_BODY_CLASS = "container-label-printing";

/** "C-00001 · Spent Solvent · Riverside Plant (CAD000123456)" — each piece
 * drops out cleanly when it isn't known yet. */
function identityLine(container: ContainerLabelData): string {
  const facilityName = container.facility?.name;
  const facilityPart =
    facilityName && container.label.epaId
      ? `${facilityName} (${container.label.epaId})`
      : facilityName;
  return [container.containerLabel, container.label.wasteStreamName, facilityPart]
    .filter((part): part is string => !!part)
    .join(" · ");
}

function AutoCreatedBadge({ generatedAt }: { generatedAt: string }): ReactElement {
  return (
    <Chip
      icon={<CheckCircle fontSize="small" />}
      label={`Auto-created · updated ${formatContainerDate(generatedAt)}`}
      color="success"
      variant="outlined"
      size="small"
    />
  );
}

function LabelScreenHeader({ container }: { container: ContainerLabelData }): ReactElement {
  return (
    <Stack
      direction="row"
      justifyContent="space-between"
      alignItems="flex-start"
      spacing={2}
      useFlexGap
      flexWrap="wrap"
    >
      <Box>
        <Typography variant="h5" component="h1" sx={{ fontWeight: 600 }}>
          Container label
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          {identityLine(container)}
        </Typography>
      </Box>
      {container.labelGeneratedAt && <AutoCreatedBadge generatedAt={container.labelGeneratedAt} />}
    </Stack>
  );
}

function PreviewCard({
  container,
  layout,
  branded,
  note,
  size,
}: {
  container: ContainerLabelData;
  layout: LabelFormat;
  branded: boolean;
  note: string;
  size: LabelSizeOption;
}): ReactElement {
  const dotIncomplete = isDotProfileIncomplete(container.label);
  const printRootClassName = [
    "container-label-print-root",
    size === "letter" ? "label-size-letter" : undefined,
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <Paper elevation={0} sx={{ p: 3 }}>
      <CardTitle>Preview</CardTitle>

      {dotIncomplete && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          DOT profile incomplete — label will print without hazard marking
        </Alert>
      )}

      <LabelPreviewStage
        printRootClassName={printRootClassName}
        content={container.label}
        layout={layout}
        branded={branded}
        noteOverride={note}
        size={PREVIEW_SIZE_BY_LABEL_SIZE[size]}
        containerType={container.containerType}
        capacity={container.capacity}
        capacityUnit={container.capacityUnit}
      />

      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 2 }}>
        Preview mode — re-renders as the format settings change. &apos;QR code only&apos; prints
        just the branded code.
      </Typography>
    </Paper>
  );
}

/** The Print & format card's mutation plumbing (save note / export),
 * split out of `ContainerLabelContent` so that component stays a plain layout. */
function useLabelActions(
  container: ContainerLabelData,
  {
    note,
    layout,
    fileType,
    branded,
  }: {
    note: string;
    layout: LabelFormat;
    fileType: LabelExportFormat;
    branded: boolean;
  }
) {
  const alerts = useAlerts();
  const [saveNote] = useMutation(SET_CONTAINER_LABEL_NOTE_MUTATION);
  const [exportLabels, { loading: exporting }] = useMutation(EXPORT_CONTAINER_LABELS_MUTATION);
  // Bridges the gap between clicking Export and `exportLabels` actually
  // firing below -- see `handleExport`. Without it, the Export button's
  // `loading` prop (driven by `exporting`, the export mutation's own
  // `loading`) would stay false while a dirty note save is in flight,
  // reading as if the click did nothing.
  const [savingNoteBeforeExport, setSavingNoteBeforeExport] = useState(false);

  // The no-op guard can't compare against `container.labelNote` -- that prop
  // stays stale until the save's response lands in the cache, so a
  // blur-refocus-blur before it resolves would see the same "unsaved" note
  // both times and fire an identical second mutation. Tracking the
  // last-submitted value in a ref (updated synchronously, before the
  // mutation call) closes that window; reverting it on error lets a retry
  // fire again.
  const lastSubmittedNoteRef = useRef(container.labelNote ?? "");
  // The save triggered by the note field's own blur -- when Export is
  // clicked with the note field focused, that blur fires (and this ref gets
  // set) synchronously before Export's own click handler runs, so
  // `commitNoteIfDirty` below sees the note as already "submitted" and
  // needs this to still await the save that's actually in flight.
  const pendingNoteSaveRef = useRef<Promise<unknown> | null>(null);

  /** Saves the note if it's changed since the last submit, returning a
   * promise that resolves once that save (new or already in flight) settles.
   * Resolves immediately when there's nothing to save. */
  const commitNoteIfDirty = (): Promise<unknown> => {
    if (note === lastSubmittedNoteRef.current) {
      return pendingNoteSaveRef.current ?? Promise.resolve();
    }
    const previouslySubmitted = lastSubmittedNoteRef.current;
    lastSubmittedNoteRef.current = note;
    const savePromise = saveNote({
      variables: {
        containerId: container.id,
        note: note.length > 0 ? note : null,
      },
      onError: (mutationError) => {
        lastSubmittedNoteRef.current = previouslySubmitted;
        alerts.error(mutationError.message || "Failed to save the label note.");
      },
    });
    pendingNoteSaveRef.current = savePromise;
    return savePromise;
  };

  const handleNoteBlur = () => {
    void commitNoteIfDirty();
  };

  const runExport = () => {
    exportLabels({
      variables: {
        containerIds: [container.id],
        layout,
        fileType,
        branded,
      },
      onCompleted: (result) => {
        const extension = fileType === LabelExportFormat.Png ? "png" : "pdf";
        void downloadFromUrl(
          result.exportContainerLabels.url,
          `container-label-${container.containerLabel}.${extension}`
        );
      },
      onError: (mutationError) => {
        alerts.error(mutationError.message || "Failed to export the label.");
      },
    });
  };

  // Clicking Export blurs the note field first (moving focus to the
  // button), which independently fires a save -- racing it against the
  // export would let the exported PDF miss a note the preview already
  // shows. Awaiting the (possibly already in-flight) save first guarantees
  // the export always reflects the note as of the click, whether it failed
  // or not -- a failed save already surfaces its own alert above, and
  // blocking the export on top of that wouldn't help the user recover.
  const handleExport = () => {
    setSavingNoteBeforeExport(true);
    void commitNoteIfDirty()
      .catch(() => undefined)
      .finally(() => {
        setSavingNoteBeforeExport(false);
        runExport();
      });
  };

  return {
    exporting: exporting || savingNoteBeforeExport,
    handleNoteBlur,
    handleExport,
    handlePrint: () => window.print(),
  };
}

function ContainerLabelContent({ container }: { container: ContainerLabelData }): ReactElement {
  const theme = useTheme();

  // Only while this screen's content is on screen should its print rules be
  // able to fire -- see the PRINT_ACTIVE_BODY_CLASS comment above.
  useEffect(() => {
    document.body.classList.add(PRINT_ACTIVE_BODY_CLASS);
    return () => {
      document.body.classList.remove(PRINT_ACTIVE_BODY_CLASS);
    };
  }, []);

  const [layout, setLayout] = useState<LabelFormat>(
    container.facility?.defaultLabelFormat ?? LabelFormat.Full
  );
  const [fileType, setFileType] = useState<LabelExportFormat>(LabelExportFormat.Pdf);
  const [size, setSize] = useState<LabelSizeOption>("4x6");
  // No UI sets this anymore -- see the "Not showing the Encamp-logo switch"
  // comment in PrintFormatCard.tsx -- but it stays real state (not a literal
  // `true`) so re-enabling the switch is just adding back a control wired to
  // it.
  const [branded] = useState(true);
  const [note, setNote] = useState(container.labelNote ?? "");

  const { exporting, handleNoteBlur, handleExport, handlePrint } = useLabelActions(container, {
    note,
    layout,
    fileType,
    branded,
  });

  return (
    <Stack
      spacing={3}
      sx={{
        px: 3,
        py: 2,
        backgroundColor: "canvas",
        minHeight: {
          xs: `calc(100vh - ${theme.spacing(xsHeight)})`,
          md: `calc(100vh - ${theme.spacing(mdHeight)})`,
        },
      }}
    >
      <LabelScreenHeader container={container} />
      <Grid container spacing={3} alignItems="flex-start">
        <Grid size={{ xs: 12, md: 4 }}>
          <PrintFormatCard
            layout={layout}
            onLayoutChange={setLayout}
            fileType={fileType}
            onFileTypeChange={setFileType}
            size={size}
            onSizeChange={setSize}
            note={note}
            onNoteChange={setNote}
            onNoteBlur={handleNoteBlur}
            onPrint={handlePrint}
            onExport={handleExport}
            exporting={exporting}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 8 }}>
          <PreviewCard
            container={container}
            layout={layout}
            branded={branded}
            note={note}
            size={size}
          />
        </Grid>
      </Grid>
    </Stack>
  );
}

/** The page's shape before its data arrives. Mirrors WasteContainerDetail's
 * skeleton -- same canvas, header rows, and two-card layout. */
function ContainerLabelSkeleton(): ReactElement {
  const theme = useTheme();
  return (
    <Stack
      spacing={3}
      sx={{
        px: 3,
        py: 2,
        backgroundColor: "canvas",
        minHeight: {
          xs: `calc(100vh - ${theme.spacing(xsHeight)})`,
          md: `calc(100vh - ${theme.spacing(mdHeight)})`,
        },
      }}
    >
      <Box>
        <Skeleton variant="text" width={220} height={34} />
        <Skeleton variant="text" width={360} height={20} sx={{ mt: 0.5 }} />
      </Box>
      <Grid container spacing={3}>
        <Grid size={{ xs: 12, md: 4 }}>
          <Paper elevation={0} sx={{ p: 3, height: 420 }}>
            <Skeleton variant="rectangular" height="100%" />
          </Paper>
        </Grid>
        <Grid size={{ xs: 12, md: 8 }}>
          <Paper elevation={0} sx={{ p: 3, height: 420 }}>
            <Skeleton variant="rectangular" height="100%" />
          </Paper>
        </Grid>
      </Grid>
    </Stack>
  );
}

export function ContainerLabelScreen(): ReactElement {
  const { containerId } = useParams<{ containerId: string }>();
  const { tenantId } = useTenant();

  const { data, loading, error } = useQuery(GET_CONTAINER_LABEL_QUERY, {
    variables: { id: containerId ?? "" },
    skip: !containerId,
  });
  const container = data?.wasteContainer;

  useBreadcrumb([
    { label: "Waste", to: tenantId ? `/o/${tenantId}/waste` : undefined },
    {
      label: "Containers",
      to: tenantId ? `/o/${tenantId}/waste/containers` : undefined,
    },
    // Nothing until the container is known, mirroring WasteContainerDetail.
    ...(container
      ? [
          {
            label: container.containerLabel,
            to: tenantId ? `/o/${tenantId}/waste/containers/${container.id}` : undefined,
          },
          { label: "Label" },
        ]
      : []),
  ]);

  if (loading && !container) {
    return <ContainerLabelSkeleton />;
  }

  if (error || !container) {
    return (
      <Stack spacing={1} sx={{ px: 3, py: 4 }} alignItems="center">
        <Typography variant="h6">Container not found</Typography>
        <Typography variant="body2" color="text.secondary">
          The container may have been archived, or the link may be incorrect.
        </Typography>
      </Stack>
    );
  }

  return <ContainerLabelContent container={container} />;
}
